<template>
  <div class="dash-card detailed-stats-widget">
    <div class="dash-card-header">
      <h3 class="dash-card-title">
        <span
          class="dash-card-title-icon"
          style="background: #e6f7ff; color: #1890ff"
        >
          <a-icon type="bar-chart" />
        </span>
        {{ $t("accueil.stat") }}
      </h3>
    </div>

    <div class="dash-card-body">
      <a-tabs v-model="activeTab" default-active-key="niveaux">
        <a-tab-pane key="niveaux" :tab="$t('personnel.niveau')">
          <a-table
            :loading="loading"
            :columns="niveauColumns"
            :data-source="niveauxWithTotal"
            :pagination="true"
            :rowClassName="setRowClass"
            bordered
          >
            <template slot="niveau" slot-scope="text, record">
              {{
                record.isTotal ? record.level : getLevelName(record.level)
              }}
            </template>
          </a-table>
        </a-tab-pane>

        <a-tab-pane key="classes" :tab="$t('accueil.classes')">
          <a-table
            :loading="loading"
            :key="tableKey"
            :columns="classColumns"
            :data-source="classesWithTotal"
            :pagination="true"
            :rowClassName="setRowClass"
            bordered
          >
            <template
              slot="filterDropdown"
              slot-scope="{
                setSelectedKeys,
                selectedKeys,
                confirm,
                clearFilters,
                column,
              }"
              style="padding: 8px"
            >
              <a-input
                v-ant-ref="(c) => (searchInput = c)"
                :placeholder="`${$t('personnel.chercher')} ${column.title}`"
                :value="selectedKeys[0]"
                style="width: 188px; margin-bottom: 8px; display: block"
                @change="
                  (e) =>
                    setSelectedKeys(e.target.value ? [e.target.value] : [])
                "
                @pressEnter="
                  () => handleSearch(selectedKeys, confirm, column.dataIndex)
                "
              />
              <a-button
                type="primary"
                icon="search"
                size="small"
                style="width: 90px; margin-right: 8px"
                @click="
                  () => handleSearch(selectedKeys, confirm, column.dataIndex)
                "
              >
                {{ $t("recette.chercher") }}
              </a-button>
              <a-button
                size="small"
                style="width: 90px"
                @click="() => handleReset(clearFilters)"
              >
                {{ $t("action.reinitialiser") }}
              </a-button>
            </template>

            <template slot="filterIcon" slot-scope="filtered">
              <a-icon
                type="search"
                :style="{ color: filtered ? '#108ee9' : undefined }"
              />
            </template>

            <template slot="customRender" slot-scope="text, record">
              {{ record.isTotal ? $t("liste.total") : text }}
              <a-tag
                v-if="record.status == 'inactive'"
                color="red"
                class="ml-2"
              >
                {{ $t("accueil.deletedClasse") }}
              </a-tag>
            </template>
          </a-table>
        </a-tab-pane>
      </a-tabs>
    </div>
  </div>
</template>

<script>
import apiClient from "@/services/axios";
import { mapState } from "vuex";
import { getAllLevels } from "@/config/LevelConstants";

export default {
  name: "DetailedStatsWidget",
  data() {
    return {
      loading: false,
      searchInput: null,
      searchText: "",
      searchedColumn: "",
      niveaux: [],
      classes: [],
      totalBoys: 0,
      totalGirls: 0,
      totalStudents: 0,
      activeTab: "niveaux",
      tableKey: 0,
    };
  },
  computed: {
    ...mapState(["settings", "user"]),
    activeBuilding() {
      return this.user.building.find(
        (el) => el.dbName == this.settings.activeBuilding
      );
    },
    countryCode() {
      return this.activeBuilding?.countryCode || "TN";
    },
    allLevels() {
      return getAllLevels(this.$t.bind(this), this.countryCode);
    },
    niveauColumns() {
      return [
        {
          title: this.$t("personnel.niveau"),
          dataIndex: "level",
          key: "niveau",
          scopedSlots: { customRender: "niveau" },
        },
        {
          title: this.$t("accueil.nombreDeClasses"),
          dataIndex: "classCount",
          key: "classCount",
        },
        {
          title: this.$t("paiement.nbEleves"),
          children: [
            {
              title: this.$t("accueil.boys"),
              dataIndex: "boyCount",
              key: "boyCount",
            },
            {
              title: this.$t("accueil.girls"),
              dataIndex: "girlCount",
              key: "girlCount",
            },
            {
              title: this.$t("liste.total"),
              dataIndex: "studentCount",
              key: "studentCount",
            },
          ],
        },
      ];
    },
    classColumns() {
      return [
        {
          title: this.$t("accueil.classes"),
          dataIndex: "name",
          key: "name",
          scopedSlots: {
            customRender: "customRender",
            filterDropdown: "filterDropdown",
            filterIcon: "filterIcon",
          },
          onFilter: (value, record) =>
            record.name.toString().toLowerCase().includes(value.toLowerCase()),
          onFilterDropdownVisibleChange: (visible) => {
            if (visible) {
              setTimeout(() => {
                this.searchInput.focus();
              }, 0);
            }
          },
        },
        {
          title: this.$t("paiement.nbEleves"),
          children: [
            {
              title: this.$t("accueil.boys"),
              dataIndex: "boys",
              key: "boys",
            },
            {
              title: this.$t("accueil.girls"),
              dataIndex: "girls",
              key: "girls",
            },
            {
              title: this.$t("liste.total"),
              dataIndex: "studentCount",
              key: "studentCount",
            },
          ],
        },
      ];
    },
    niveauxWithTotal() {
      const totalRow = {
        level: this.$t("liste.total"),
        classCount: this.niveaux.reduce(
          (sum, level) => sum + level.classCount,
          0
        ),
        boyCount: this.totalBoys,
        girlCount: this.totalGirls,
        studentCount: this.totalStudents,
        isTotal: true,
      };

      return [...this.niveaux, totalRow];
    },
    classesWithTotal() {
      if (!this.classes.length) return [];

      const totalRow = {
        name: this.$t("liste.total"),
        boys: this.totalBoys,
        girls: this.totalGirls,
        studentCount: this.totalStudents,
        isTotal: true,
      };

      return [...this.classes, totalRow];
    },
  },
  created() {
    this.loadStats();
  },
  methods: {
    async loadStats() {
      this.loading = true;
      try {
        const { data } = await apiClient.get("/home/dashboard", {});
        this.classes = data.classes || [];
        this.niveaux = data.niveaux || [];
        this.totalStudents = data.totalStudents || 0;
        this.totalBoys = data.totalBoys || 0;
        this.totalGirls = data.totalGirls || 0;
      } catch (error) {
        console.error("Error loading detailed stats:", error);
      } finally {
        this.loading = false;
      }
    },
    setRowClass(record) {
      return record.isTotal ? "stats-total-row" : "";
    },
    handleSearch(selectedKeys, confirm, dataIndex) {
      confirm();
      this.searchText = selectedKeys[0];
      this.searchedColumn = dataIndex;
    },
    handleReset(clearFilters) {
      clearFilters();
      this.searchText = "";
    },
    getLevelName(level) {
      return this.allLevels[String(level)] || level;
    },
  },
};
</script>

<style lang="scss" scoped>
.detailed-stats-widget {
  ::v-deep .stats-total-row {
    background-color: #f5f5f5;
    font-weight: bold;
  }
}

[data-kit-theme="dark"] {
  .detailed-stats-widget {
    ::v-deep .stats-total-row {
      background-color: #141322;
    }
  }
}
</style>
